import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Menu,
  User as UserIcon,
  LogOut,
  ChevronDown,
  Search,
  Bell,
  CreditCard,
  TrendingUp,
  ShieldCheck,
  Check,
  Calendar,
  X
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import {
  useGetProfileQuery,
  useGetAdminUserListQuery,
  useGetAllProjectsQuery,
  useGetMarketplaceListingsQuery,
  useGetAllAdminsQuery,
  useGetMarketplaceKycRequestsQuery,
  useGetWalletTransactionsQuery,
  useGetAllInvestmentsQuery,
  useGetContactSubmissionsQuery,
  useGetDealersQuery
} from '../../store/slices/apiSlice';
import { formatDistanceToNow } from '../../utils/dateUtils';
import toast from 'react-hot-toast';

interface HeaderProps {
  onMenuClick?: () => void;
}

// ── Searchable Items Map for Global Search ─────────────────────────────────────
interface SearchableItem {
  title: string;
  category: 'Pages' | 'Projects' | 'Listings' | 'Users';
  url: string;
}

const SEARCHABLE_ITEMS: SearchableItem[] = [
  { title: 'Dashboard Overview', category: 'Pages', url: '/admin' },
  { title: 'Manage Users & Members', category: 'Pages', url: '/admin/users' },
  { title: 'Verify Dealers & KYC', category: 'Pages', url: '/admin/dealer-management' },
  { title: 'System Admins', category: 'Pages', url: '/admin/admin-users' },
  { title: 'Investment Projects', category: 'Pages', url: '/admin/projects' },
  { title: 'Create Investment Project', category: 'Pages', url: '/admin/create-project' },
  { title: 'Investments Records & History', category: 'Pages', url: '/admin/investments' },
  { title: 'Apartments Management', category: 'Pages', url: '/admin/apartments' },
  { title: 'Bookings List', category: 'Pages', url: '/admin/bookings' },
  { title: 'App Ads Slider', category: 'Pages', url: '/admin/cta-management' },
  { title: 'Add Ads Slider', category: 'Pages', url: '/admin/cta-management/add' },
  { title: 'Blogs & Articles', category: 'Pages', url: '/admin/blogs' },
  { title: 'Add New Blog', category: 'Pages', url: '/admin/blogs/add' },
  { title: 'Solar Leads Requests', category: 'Pages', url: '/admin/solar-leads' },
  { title: 'Contact Submission Inbox', category: 'Pages', url: '/admin/contact-submissions' },
  
  // Real Mock Content matches standard projects/listings seen in screenshots
  { title: 'TOYOTA CAMRY (Automobile Listing)', category: 'Listings', url: '/admin/marketplace-listings' },
  { title: 'Diasporafarms Fishery Project (Active)', category: 'Projects', url: '/admin/projects' },
  { title: 'Washnbox LLC Bond 1 (Draft)', category: 'Projects', url: '/admin/projects' },
  { title: 'Diasporahomes @ Royalflex Estate 13', category: 'Projects', url: '/admin/projects' },
  { title: 'Amit Carpenter (Admin User)', category: 'Users', url: '/admin/admin-users' },
  { title: 'diasporawise.admin@gmail.com', category: 'Users', url: '/admin/admin-users' },
];

// ── Intent Notification Interfaces ──────────────────────────────────────────────
interface IntentNotification {
  id: string;
  type: 'dealer' | 'transaction' | 'investment' | 'general';
  title: string;
  description: string;
  time: string;
  isRead: boolean;
  link: string;
  rawTime?: number;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { data: profile, isLoading } = useGetProfileQuery({ role: user?.role || '' });
  const navigate = useNavigate();
  const location = useLocation();

  // State Management
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Local storage read notification tracking
  const [readNotificationIds, setReadNotificationIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('read_notification_ids');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Global Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  // Dynamic search API queries
  const { data: usersRes } = useGetAdminUserListQuery(
    { search: searchQuery, limit: 10 },
    { skip: !searchQuery.trim() }
  );
  const { data: projectsRes } = useGetAllProjectsQuery(
    { search: searchQuery, limit: 10 },
    { skip: !searchQuery.trim() }
  );
  const { data: listingsRes } = useGetMarketplaceListingsQuery(
    { search: searchQuery, limit: 10 },
    { skip: !searchQuery.trim() }
  );
  const { data: adminsRes } = useGetAllAdminsQuery(
    undefined,
    { skip: !searchQuery.trim() }
  );

  // Dynamic intent notification queries (with 5s real-time polling)
  const { data: dealersRes } = useGetDealersQuery({ page: 1, limit: 10, verification_status: 'pending' }, { pollingInterval: 5000 });
  const { data: kycRes } = useGetMarketplaceKycRequestsQuery({ page: 1, limit: 10, status: 'pending' }, { pollingInterval: 5000 });
  const { data: walletRes } = useGetWalletTransactionsQuery({ page: 1, limit: 10, status: 'pending' }, { pollingInterval: 5000 });
  const { data: investmentsRes } = useGetAllInvestmentsQuery(undefined, { pollingInterval: 5000 });
  const { data: contactRes } = useGetContactSubmissionsQuery({ page: 1, limit: 10, status: 'pending' }, { pollingInterval: 5000 });

  const pendingDealers = dealersRes?.data?.dealers || [];
  const kycRequests = kycRes?.data?.requests || [];
  const walletTransactions = walletRes?.data?.transactions || [];
  const investments = investmentsRes?.data || [];
  const contactSubmissions = contactRes?.data?.submissions || [];

  // Map and unify notifications list
  const notifications = React.useMemo(() => {
    const list: IntentNotification[] = [];

    // 1. Map Pending Dealer Registrations
    pendingDealers.forEach(dealer => {
      const id = `dealer-pending-${dealer.dealer_id}`;
      const timeAgo = formatDistanceToNow(dealer.created_at);
      list.push({
        id,
        type: 'dealer',
        title: 'New Dealer Verification Request',
        description: `Dealer "${dealer.name}" (${dealer.business_name || dealer.email}) registered as ${dealer.dealer_type} dealer and requires verification.`,
        time: timeAgo === 'just now' ? timeAgo : `${timeAgo} ago`,
        isRead: readNotificationIds.includes(id),
        link: `/admin/dealer-management`,
        rawTime: new Date(dealer.created_at).getTime()
      });
    });

    // 2. Map KYC requests
    kycRequests.forEach(req => {
      const id = `kyc-${req.kyc_request_id}`;
      const timeAgo = formatDistanceToNow(req.created_at);
      list.push({
        id,
        type: 'dealer',
        title: 'New Dealer KYC Approval',
        description: `Dealer "${req.user?.name || 'Unknown'}" (${req.user?.email || ''}) uploaded registration docs.`,
        time: timeAgo === 'just now' ? timeAgo : `${timeAgo} ago`,
        isRead: readNotificationIds.includes(id),
        link: `/admin/marketplace-kyc-requests/view/${req.kyc_request_id}`,
        rawTime: new Date(req.created_at).getTime()
      });
    });

    // 2. Map wallet transactions
    walletTransactions.forEach(tx => {
      const id = `tx-${tx.transaction_id}`;
      const timeAgo = formatDistanceToNow(tx.created_at);
      list.push({
        id,
        type: 'transaction',
        title: 'New Transaction Pending',
        description: `${tx.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ₦${tx.amount.toLocaleString()} recorded via ${tx.payment_method || 'Bank Transfer'} requires admin audit.`,
        time: timeAgo === 'just now' ? timeAgo : `${timeAgo} ago`,
        isRead: readNotificationIds.includes(id),
        link: '/admin/wallet',
        rawTime: new Date(tx.created_at).getTime()
      });
    });

    // 3. Map pending investments
    investments
      .filter(inv => inv.status === 'pending')
      .forEach(inv => {
        const id = `inv-${inv.investment_id}`;
        const timeAgo = formatDistanceToNow(inv.created_at);
        list.push({
          id,
          type: 'investment',
          title: 'New Investment Recorded',
          description: `User "${inv.user?.name || 'Unknown'}" invested ₦${parseFloat(inv.amount_invested).toLocaleString()} in ${inv.project?.title || 'Project'}.`,
          time: timeAgo === 'just now' ? timeAgo : `${timeAgo} ago`,
          isRead: readNotificationIds.includes(id),
          link: '/admin/investments',
          rawTime: new Date(inv.created_at).getTime()
        });
      });

    // 4. Map contact submissions
    contactSubmissions.forEach(sub => {
      const id = `contact-${sub.id}`;
      const timeAgo = formatDistanceToNow(sub.created_at);
      list.push({
        id,
        type: 'general',
        title: 'New Contact Submission',
        description: `Subject: "${sub.subject}" from ${sub.name || 'Unknown'}.`,
        time: timeAgo === 'just now' ? timeAgo : `${timeAgo} ago`,
        isRead: readNotificationIds.includes(id),
        link: '/admin/contact-submissions',
        rawTime: new Date(sub.created_at).getTime()
      });
    });

    // Sort newest first
    return list.sort((a, b) => (b.rawTime || 0) - (a.rawTime || 0));
  }, [kycRequests, walletTransactions, investments, contactSubmissions, readNotificationIds]);
  
  // Refs for closing on click outside
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Compute unread notification count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully!');
    navigate('/login');
    setUserMenuOpen(false);
  };

  // Close menus on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Profile metadata helpers
  const getProfileImage = () => {
    if (isLoading) return null;
    return profile?.data?.profile_image || null;
  };

  const getUserInitial = () => {
    return profile?.data?.name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'A';
  };

  const markAllNotificationsRead = () => {
    const allIds = notifications.map(n => n.id);
    const updated = Array.from(new Set([...readNotificationIds, ...allIds]));
    setReadNotificationIds(updated);
    try {
      localStorage.setItem('read_notification_ids', JSON.stringify(updated));
    } catch (err) {
      console.error('Failed to save read notifications to localStorage', err);
    }
    toast.success('All notifications marked as read!');
  };

  const handleNotificationClick = (item: IntentNotification) => {
    if (!readNotificationIds.includes(item.id)) {
      const updated = [...readNotificationIds, item.id];
      setReadNotificationIds(updated);
      try {
        localStorage.setItem('read_notification_ids', JSON.stringify(updated));
      } catch (err) {
        console.error('Failed to save read notifications to localStorage', err);
      }
    }
    setNotificationsOpen(false);
    navigate(item.link);
  };

  // Filter global search results dynamically combining static pages and API results
  const filteredSearchItems = React.useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();

    // 1. Filter static pages
    const pageMatches = SEARCHABLE_ITEMS
      .filter(item => item.category === 'Pages')
      .filter(item => item.title.toLowerCase().includes(query))
      .map(item => ({
        title: item.title,
        category: 'Pages' as const,
        url: item.url
      }));

    // 2. Map dynamic Users from API
    const userMatches = (usersRes?.data || [])
      .map(user => ({
        title: user.name ? `${user.name} (${user.email})` : user.email,
        category: 'Users' as const,
        url: `/admin/user-details/${user.user_id}`
      }));

    // 3. Map dynamic Admins from API (client-side filter)
    const adminMatches = (adminsRes?.data || [])
      .filter(admin => 
        admin.name.toLowerCase().includes(query) || 
        admin.email.toLowerCase().includes(query)
      )
      .map(admin => ({
        title: `${admin.name} (${admin.email}) [Admin]`,
        category: 'Users' as const,
        url: '/admin/admin-users'
      }));

    // 4. Map dynamic Projects from API
    const projectMatches = (projectsRes?.data || [])
      .map(proj => ({
        title: proj.title,
        category: 'Projects' as const,
        url: '/admin/projects'
      }));

    // 5. Map dynamic Listings from API
    const listingMatches = (listingsRes?.data?.listings || [])
      .map(list => ({
        title: list.title,
        category: 'Listings' as const,
        url: '/admin/marketplace-listings'
      }));

    return [...pageMatches, ...userMatches, ...adminMatches, ...projectMatches, ...listingMatches];
  }, [searchQuery, usersRes, adminsRes, projectsRes, listingsRes]);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/' || path === '/admin/reports') return 'Dashboard';
    if (path.startsWith('/admin/create-project')) return 'Add Project';
    if (path.startsWith('/admin/projects/view')) return 'Project Details';
    if (path.startsWith('/admin/projects')) return 'Projects';
    if (path.startsWith('/admin/update-project')) return 'Edit Project';
    if (path.startsWith('/admin/investments')) return 'Investments';
    if (path.startsWith('/admin/admin-users')) return 'Admins';
    if (path.startsWith('/admin/user-details')) return 'User Details';
    if (path.startsWith('/admin/users')) return 'Users';
    if (path.startsWith('/admin/categories')) return 'Categories';
    if (path.startsWith('/admin/bank-accounts')) return 'Bank Accounts';
    if (path.startsWith('/admin/wallet')) return 'Wallet';
    if (path.startsWith('/admin/cta-management/add')) return 'Add App Ads Slider';
    if (path.startsWith('/admin/cta-management/edit')) return 'Edit App Ads Slider';
    if (path.startsWith('/admin/cta-management')) return 'App Ads Slider';
    if (path.startsWith('/admin/blogs/add')) return 'Add Blog';
    if (path.startsWith('/admin/blogs/edit')) return 'Edit Blog';
    if (path.startsWith('/admin/blogs/view')) return 'View Blog';
    if (path.startsWith('/admin/blogs')) return 'Blogs';
    if (path.startsWith('/admin/jobs/add')) return 'Add Job';
    if (path.startsWith('/admin/jobs/edit')) return 'Edit Job';
    if (path.startsWith('/admin/jobs/applications')) return 'Job Applications';
    if (path.startsWith('/admin/jobs')) return 'Jobs';
    if (path.startsWith('/admin/testimonials/create')) return 'Create Testimonial';
    if (path.startsWith('/admin/testimonials/edit')) return 'Edit Testimonial';
    if (path.startsWith('/admin/testimonials')) return 'Testimonials';
    if (path.startsWith('/admin/payment-methods')) return 'Payment Methods';
    if (path.startsWith('/admin/super-admin-profile')) return 'Super Admin Profile';
    if (path.startsWith('/admin/profile')) return 'My Profile';
    if (path.startsWith('/admin/notifications')) return 'Notifications';
    if (path.startsWith('/admin/contact-submissions/')) return 'Submission Details';
    if (path.startsWith('/admin/contact-submissions')) return 'Contact Submissions';
    if (path.startsWith('/admin/solar-leads/')) return 'Solar Lead Details';
    if (path.startsWith('/admin/solar-leads')) return 'Solar Leads';
    if (path.startsWith('/admin/child-sponsorship/create')) return 'Add Sponsored Child';
    if (path.startsWith('/admin/child-sponsorship/edit')) return 'Edit Sponsored Child';
    if (path.startsWith('/admin/child-sponsorship/updates/create')) return 'Create Sponsorship Update';
    if (path.startsWith('/admin/child-sponsorship/updates/edit')) return 'Edit Sponsorship Update';
    if (path.startsWith('/admin/child-sponsorship/updates')) return 'Sponsorship Updates';
    if (path.startsWith('/admin/child-sponsorship/')) return 'Child Details';
    if (path.startsWith('/admin/child-sponsorship')) return 'Child Sponsorship';
    if (path.startsWith('/admin/membership-management/create')) return 'Create Membership Plan';
    if (path.startsWith('/admin/membership-management/edit')) return 'Edit Membership Plan';
    if (path.startsWith('/admin/membership-management/view')) return 'View Membership Plan';
    if (path.startsWith('/admin/membership-management')) return 'Membership Plans';
    if (path.startsWith('/admin/memberships/create')) return 'Create Subscription';
    if (path.startsWith('/admin/memberships/view')) return 'View Subscription';
    if (path.startsWith('/admin/memberships/edit')) return 'Edit Subscription';
    if (path.startsWith('/admin/memberships')) return 'Subscriptions';
    if (path.startsWith('/admin/apartments/create')) return 'Create Apartment';
    if (path.startsWith('/admin/apartments/view')) return 'View Apartment';
    if (path.startsWith('/admin/apartments/edit')) return 'Edit Apartment';
    if (path.startsWith('/admin/apartments')) return 'Apartments';
    if (path.startsWith('/admin/bookings/')) return 'Booking Details';
    if (path.startsWith('/admin/bookings')) return 'Bookings';
    if (path.startsWith('/admin/marketplace-listings/create')) return 'Create Listing';
    if (path.startsWith('/admin/marketplace-listings/view')) return 'View Listing';
    if (path.startsWith('/admin/marketplace-listings/edit')) return 'Edit Listing';
    if (path.startsWith('/admin/marketplace-listings')) return 'Marketplace Listings';
    if (path.startsWith('/admin/marketplace-inquiries/view')) return 'Inquiry Details';
    if (path.startsWith('/admin/marketplace-inquiries')) return 'Marketplace Inquiries';
    if (path.startsWith('/admin/marketplace-transactions/view')) return 'Transaction Details';
    if (path.startsWith('/admin/marketplace-transactions')) return 'Marketplace Transactions';
    if (path.startsWith('/admin/marketplace-settings')) return 'Marketplace Settings';
    if (path.startsWith('/admin/marketplace-kyc-requests/view')) return 'KYC Details';
    if (path.startsWith('/admin/marketplace-kyc-requests')) return 'Verify Dealers';
    if (path.startsWith('/admin/dealer-management/')) return 'Dealer Details';
    if (path.startsWith('/admin/dealer-management')) return 'Car Dealers';
    if (path.startsWith('/admin/sold-listings')) return 'Sold Listings';
    if (path.startsWith('/admin/pending-listings')) return 'Pending Listings';
    return 'Diasporawise Admin';
  };

  // Helper to render type-specific icons inside Notification items
  const getNotificationIcon = (type: IntentNotification['type']) => {
    switch (type) {
      case 'dealer':
        return (
          <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0">
            <ShieldCheck size={16} />
          </div>
        );
      case 'transaction':
        return (
          <div className="w-8 h-8 rounded-xl bg-green-100 flex items-center justify-center text-green-600 flex-shrink-0">
            <CreditCard size={16} />
          </div>
        );
      case 'investment':
        return (
          <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
            <TrendingUp size={16} />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
            <Bell size={16} />
          </div>
        );
    }
  };

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-30">
      <div className="px-4 md:px-6 lg:px-8 py-3.5">
        <div className="flex items-center justify-between gap-4">
          
          {/* ── Left Section: Menu Toggle & Title ──────────────────────────────── */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {onMenuClick && (
              <button
                className="lg:hidden p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 group"
                onClick={onMenuClick}
              >
                <Menu className="h-5 w-5 text-gray-600 group-hover:text-gray-900" />
              </button>
            )}
            <h1 className="hidden sm:block text-lg font-bold text-gray-800 md:text-xl">
              {getPageTitle()}
            </h1>
          </div>

          {/* ── Middle Section: Global Search Bar ──────────────────────────────── */}
          <div className="flex-1 max-w-md mx-auto relative" ref={searchRef}>
            <div className={`flex items-center w-full px-3.5 py-2 rounded-xl border transition-all duration-200 ${
              searchFocused 
                ? 'bg-white border-blue-500 shadow-md ring-2 ring-blue-100' 
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100/50'
            }`}>
              <Search className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                placeholder="Search listings, projects, users..."
                onFocus={() => setSearchFocused(true)}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="p-1 hover:bg-gray-200 rounded-full text-gray-400 hover:text-gray-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Global Search Results Dropdown Overlay */}
            {searchFocused && searchQuery.trim() && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-2xl z-50 max-h-[380px] overflow-y-auto p-2">
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">
                  Search Results ({filteredSearchItems.length})
                </div>
                {filteredSearchItems.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {filteredSearchItems.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSearchQuery('');
                          setSearchFocused(false);
                          navigate(item.url);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-blue-50/70 rounded-xl text-left transition-colors duration-150 group"
                      >
                        <div className="min-w-0 flex-1 pr-3">
                          <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 truncate">
                            {item.title}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            Route: {item.url}
                          </p>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wider scale-90">
                          {item.category}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                    No results match "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Right Section: Notifications & Profile ─────────────────────────── */}
          <div className="flex items-center gap-3.5 flex-shrink-0">
            
            {/* Notification Bell Dropdown */}
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className={`p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 relative group ${
                  notificationsOpen ? 'bg-gray-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Bell size={20} className="transition-transform duration-200 group-hover:scale-105" />
                {unreadCount > 0 && (
                  <>
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center border border-white">
                      {unreadCount}
                    </span>
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 rounded-full border border-white animate-ping opacity-75 pointer-events-none" />
                  </>
                )}
              </button>

              {/* Notification Overlay Pop-up Card */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2.5 w-[380px] bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-in fade-in slide-in-from-top-3 duration-250">
                  {/* Pop-up Header */}
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-bold text-gray-900">Notifications</h2>
                      <p className="text-xs text-gray-500 mt-0.5">{unreadCount} pending admin approvals</p>
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllNotificationsRead}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 hover:underline"
                      >
                        <Check size={12} />
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* Pop-up Body (Scrollable List) */}
                  <div className="overflow-y-auto max-h-[340px] divide-y divide-gray-50">
                    {notifications.length > 0 ? (
                      notifications.map(item => (
                        <div
                          key={item.id}
                          onClick={() => handleNotificationClick(item)}
                          className={`p-4 flex gap-3 cursor-pointer transition-all duration-200 hover:bg-blue-50/50 ${
                            !item.isRead ? 'bg-blue-50/20 font-medium' : ''
                          }`}
                        >
                          {getNotificationIcon(item.type)}
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-1">
                              <p className={`text-sm text-gray-800 ${!item.isRead ? 'font-bold' : 'font-medium'}`}>
                                {item.title}
                              </p>
                              {!item.isRead && (
                                <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1 leading-normal line-clamp-2">
                              {item.description}
                            </p>
                            <span className="text-[10px] text-gray-400 mt-2 block">
                              {item.time}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-sm text-gray-500">
                        No notifications found
                      </div>
                    )}
                  </div>

                  {/* Pop-up Footer */}
                  <div className="p-3 bg-gray-50/50 border-t border-gray-100 text-center">
                    <button
                      onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/admin/notifications');
                      }}
                      className="text-xs font-bold text-gray-600 hover:text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      <Calendar size={12} />
                      See all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Vertical Splitter */}
            <div className="h-6 w-px bg-gray-200"></div>

            {/* User Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 sm:gap-3 p-1.5 hover:bg-gray-100 rounded-xl transition-all duration-200"
              >
                <div className="relative w-8 h-8 rounded-full overflow-hidden">
                  {getProfileImage() ? (
                    <>
                      <img
                        src={getProfileImage()!}
                        alt={profile?.data?.name || user?.name || 'User'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div
                        className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm"
                        style={{ display: 'none' }}
                      >
                        {getUserInitial()}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                      <UserIcon className="w-4 h-4" />
                    </div>
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {profile?.data?.name || user?.name || 'Admin User'}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {user?.role === 'admin' ? 'Admin' : user?.role === 'manager' ? 'Manager' : user?.role === 'exclusive' ? 'Exclusive' : user?.role || 'Admin'}
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User Menu Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2.5 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="relative w-12 h-12 rounded-full overflow-hidden">
                        {getProfileImage() ? (
                          <>
                            <img
                              src={getProfileImage()!}
                              alt={profile?.data?.name || user?.name || 'User'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div
                              className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold"
                              style={{ display: 'none' }}
                            >
                              {getUserInitial()}
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                            <UserIcon className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {profile?.data?.name || user?.name || 'Admin User'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {profile?.data?.email || user?.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <button
                      onClick={() => {
                        navigate('/admin/profile');
                        setUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 text-sm"
                    >
                      <UserIcon className="w-4 h-4" />
                      <span>Profile</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-100 p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

        </div>
      </div>
    </header>
  );
};

export default Header;